// src/hooks/useKeyboardShortcuts.ts
/**
 * Keyboard Shortcuts Hook for T-24 Preferences
 * 
 * Handles keyboard shortcuts for various app functions including preferences
 */

import { useEffect, useCallback } from 'react';
import { useAppDispatch, usePreferences } from './redux';
import { toggleTheme } from '../store/slices/preferencesSlice';
import { setScale } from '../store/slices/viewportSlice';
import { useDialog, useSidePanel } from '../components/ui';
import { ShareDialogContent } from '../components/ShareDialogContent';
import { LoadDialogContent } from '../components/LoadDialogContent';
import { PreferencesContent } from '../components/PreferencesContent';

export interface KeyboardShortcuts {
  'ctrl+,': () => void;           // Open preferences
  'ctrl+alt+t': () => void;       // Toggle theme
  'ctrl+alt+s': () => void;       // Share dialog
  'ctrl+alt+l': () => void;       // Load dialog
  'ctrl+=': () => void;           // Zoom in
  'ctrl+-': () => void;           // Zoom out
  'ctrl+0': () => void;           // Reset zoom
  'escape': () => void;           // Close dialogs
}

/**
 * Custom hook for managing keyboard shortcuts
 */
export const useKeyboardShortcuts = () => {
  const dispatch = useAppDispatch();
  const preferences = usePreferences();
  
  // New UI framework hooks
  const { openDialog, closeDialog } = useDialog();
  const { openSidePanel, closeSidePanel } = useSidePanel();

  // Define shortcut handlers
  const shortcuts: KeyboardShortcuts = {
    'ctrl+,': useCallback(() => {
      openSidePanel(<PreferencesContent />, "Preferences");
    }, [openSidePanel]),

    'ctrl+alt+t': useCallback(() => {
      dispatch(toggleTheme());
    }, [dispatch]),

    'ctrl+alt+s': useCallback(() => {
      openDialog(<ShareDialogContent />, "Share Number Line", { size: 'md' });
    }, [openDialog]),

    'ctrl+alt+l': useCallback(() => {
      openDialog(<LoadDialogContent />, "Load Shared Scene", { size: 'md' });
    }, [openDialog]),

    'ctrl+=': useCallback(() => {
      // Zoom in - we'll need to get current scale from state
      // This will be handled by the component that uses this hook
    }, []),

    'ctrl+-': useCallback(() => {
      // Zoom out - we'll need to get current scale from state
      // This will be handled by the component that uses this hook
    }, []),

    'ctrl+0': useCallback(() => {
      // Reset zoom to default
      dispatch(setScale(100));
    }, [dispatch]),

    'escape': useCallback(() => {
      // Close any open modals
      closeDialog();
      closeSidePanel();
    }, [closeDialog, closeSidePanel]),
  };

  // Convert keyboard event to shortcut string
  const getShortcutString = useCallback((event: KeyboardEvent): string => {
    const parts: string[] = [];
    
    if (event.ctrlKey || event.metaKey) parts.push('ctrl');
    if (event.shiftKey) parts.push('shift');
    if (event.altKey) parts.push('alt');
    
    // Handle special keys
    const key = event.key.toLowerCase();
    if (key === 'escape') {
      parts.push('escape');
    } else if (key === ',') {
      parts.push(',');
    } else if (key === '=' || key === '+') {
      parts.push('=');
    } else if (key === '-') {
      parts.push('-');
    } else if (key === '0') {
      parts.push('0');
    } else if (key.length === 1) {
      parts.push(key);
    }
    
    return parts.join('+');
  }, []);

  // Handle keyboard events
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Only handle shortcuts if keyboard shortcuts are enabled
    if (!preferences.keyboardShortcuts) {
      return;
    }

    // Don't handle shortcuts when typing in inputs
    const target = event.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable
    ) {
      // Exception: Allow Escape to work in inputs
      if (event.key === 'Escape') {
        target.blur();
        shortcuts.escape();
        event.preventDefault();
      }
      return;
    }

    const shortcutString = getShortcutString(event);
    const handler = shortcuts[shortcutString as keyof KeyboardShortcuts];
    
    if (handler) {
      handler();
      event.preventDefault();
    }
  }, [preferences.keyboardShortcuts, shortcuts, getShortcutString]);

  // Set up event listeners
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return shortcuts;
};

// Keyboard shortcuts helper component
export const KeyboardShortcutsProvider: React.FC<{ children: React.ReactNode }> = ({ 
  children 
}) => {
  useKeyboardShortcuts();
  return <>{children}</>;
};